import { useState, useContext, useEffect, useCallback } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { onSnapshot, doc, collection } from "firebase/firestore";
import { useAuth, useUser } from "@clerk/clerk-react";

import { db, firebaseAuth } from "index";
import axiosWithToken from "axiosWithToken";

import { ParentContext } from "components/constants/ParentContext";
import { mixpanelCustomEvent } from "components/mixpanel/eventTriggers";
import { MixpanelEventName } from "components/mixpanel/types";

import {
  FIRESTORE_COLLECTIONS,
  FIRESTORE_ORGANIZATION_SUB_COLLECTIONS,
} from "types";
import { useSubscription } from "./useSubscription";
import { getNotificationsCount } from "layout/sidebar/notifications/service";
import { useDispatch } from "react-redux";
import { setNotificationCount } from "layout/sidebar/notifications/notificationSlice";
import { setDomainSettingsDetails } from "components/domain-settings/domainSettingsSlice";
import { getDomainSettings } from "components/domain-settings/service";

export function useAppState() {
  const context = useContext(ParentContext);
  const dispatch = useDispatch();

  const { fetchCurrentSubscription } = useSubscription();
  const user = JSON.parse(localStorage.getItem("user")!);

  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [succesText, setSuccessText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [credits, setCredits] = useState<any>(null);

  const [isBiz, setIsBiz] = useState(() => {
    const harbour = context?.isHarbour ?? user?.isHarbourClub ?? false;
    const biz4biz = context?.isBiz4Biz ?? user?.isBiz4Biz ?? false;
    return !harbour && !biz4biz;
  });
  const [isHarbourClub, setIsHarbourClub] = useState(
    context?.isHarbour ?? user?.isHarbourClub ?? false
  );
  const [isBiz4Biz, setIsBiz4Biz] = useState(
    context?.isBiz4Biz ?? user?.isBiz4Biz ?? false
  );
  const [tutorialStep, setTutorialStep] = useState(context?.tutorialStep || "");

  const { getToken } = useAuth();
  const { isSignedIn } = useUser();
  const baseURL2 = process.env.REACT_APP_BASEURL2;

  useEffect(() => {
    if (!isSignedIn || !user?.organizationId) {
      return;
    }

    const domainSettingsRef = collection(
      db,
      FIRESTORE_COLLECTIONS.ORGANIZATIONS,
      user.organizationId,
      FIRESTORE_ORGANIZATION_SUB_COLLECTIONS.DOMAIN_SETTINGS
    );

    const unsubscribe = onSnapshot(domainSettingsRef, async () => {
      try {
        const domainSettings = await getDomainSettings();
        if (domainSettings && Array.isArray(domainSettings)) {
          dispatch(setDomainSettingsDetails(domainSettings));
        }
      } catch (error) {
        console.error("Failed to sync domain settings:", error);
      }
    });

    return () => unsubscribe();
  }, [isSignedIn, user?.organizationId, dispatch]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const redirectFromCheckout =
      urlParams.get("redirectFromCheckout") === "true";

    if (redirectFromCheckout) {
      setShowProcessingModal(true);
    }

    const orgRef = user?.organizationId
      ? doc(db, FIRESTORE_COLLECTIONS.ORGANIZATIONS, user.organizationId)
      : null;
    let lastUpdatedOrgQuota = localStorage.getItem("quotaLastChangedAt");

    const unsubscribe = orgRef
      ? onSnapshot(orgRef, (docSnapshot: any) => {
          if (docSnapshot.exists()) {
            const oragnizationData = docSnapshot.data();

            localStorage.setItem(
              "quotaLastChangedAt",
              oragnizationData.quotaLastChangedAt?.seconds
            );

            if (
              !lastUpdatedOrgQuota ||
              lastUpdatedOrgQuota === undefined ||
              lastUpdatedOrgQuota === "undefined"
            ) {
              lastUpdatedOrgQuota =
                oragnizationData.quotaLastChangedAt?.seconds;
            }

            if (
              lastUpdatedOrgQuota &&
              Number(lastUpdatedOrgQuota) <=
                oragnizationData.quotaLastChangedAt?.seconds
            ) {
              fetchCurrentSubscription(false);

              mixpanelCustomEvent({
                mixpanelProps: {},
                id: "",
                eventName: MixpanelEventName.snapshotUpdated,
              });

              if (redirectFromCheckout) {
                setShowProcessingModal(false);
                const newUrl = window.location.pathname;
                window.location.href = newUrl;
              }
            }
          }
        })
      : () => {};

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.uid]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    const userRef = user?.uid
      ? doc(db, FIRESTORE_COLLECTIONS.USERS, user.uid)
      : null;

    let lastUpdatedAt = localStorage.getItem("updatedAt");

    const unsubscribe = userRef
      ? onSnapshot(userRef, async (docSnapshot: any) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();

            localStorage.setItem("updatedAt", userData.updatedAt?.seconds);

            if (
              !lastUpdatedAt ||
              lastUpdatedAt === undefined ||
              lastUpdatedAt === "undefined"
            ) {
              lastUpdatedAt = userData.updatedAt?.seconds;
            }

            if (
              lastUpdatedAt &&
              Number(lastUpdatedAt) <= userData.updatedAt?.seconds
            ) {
              try {
                const userDataResponse = await axiosWithToken.get(
                  `${baseURL2}api/auth/user`
                );
                if (userDataResponse) {
                  localStorage.setItem(
                    "user",
                    JSON.stringify(userDataResponse.data)
                  );
                }
              } catch (error) {
                console.error("Failed to fetch user data:", error);
              }
            }
          }
        })
      : () => {};

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.uid]);

  useEffect(() => {
    if (!isSignedIn) {
      return;
    }

    const userRef = user?.uid
      ? doc(db, FIRESTORE_COLLECTIONS.USERS, user.uid)
      : null;

    let notificationVersion = localStorage.getItem("notificationVersion");

    const unsubscribe = userRef
      ? onSnapshot(userRef, async (docSnapshot: any) => {
          if (docSnapshot.exists()) {
            const userData = docSnapshot.data();

            localStorage.setItem(
              "notificationVersion",
              userData.notificationVersion
            );

            if (
              !notificationVersion ||
              notificationVersion === undefined ||
              notificationVersion === "undefined"
            ) {
              notificationVersion = userData.notificationVersion;
            }

            if (
              notificationVersion &&
              notificationVersion < userData.notificationVersion
            ) {
              try {
                const data = await getNotificationsCount();
                dispatch(setNotificationCount(data.count));
              } catch (error) {
                console.error("Failed to fetch user data:", error);
              }
            }
          }
        })
      : () => {};

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.uid]);

  const syncFirebaseAuth = useCallback(async () => {
    try {
      const response = await axiosWithToken.post(
        `${baseURL2}api/auth/firebase/custom-token`
      );
      if (response?.data && firebaseAuth) {
        await signInWithCustomToken(firebaseAuth, response.data.token);
      }
    } catch (error) {
      console.error("Token sync error:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  useEffect(() => {
    if (!isSignedIn) return;

    let intervalId: NodeJS.Timeout;

    const startTokenRefreshLoop = async () => {
      await syncFirebaseAuth();
      intervalId = setInterval(() => {
        syncFirebaseAuth();
      }, 55 * 60 * 1000);
    };

    startTokenRefreshLoop();

    return () => clearInterval(intervalId);
  }, [isSignedIn, syncFirebaseAuth]);

  const successClicked = async (text: string) => {
    setSuccessText(text);
    setShowSuccessToast(true);
    await delay(5000);
    setShowSuccessToast(false);
  };

  const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

  return {
    showSuccessToast,
    setShowSuccessToast,
    succesText,
    setSuccessText,
    isLoading,
    setIsLoading,
    showProcessingModal,
    setShowProcessingModal,
    credits,
    setCredits,
    successClicked,
    isBiz,
    setIsBiz,
    isHarbourClub,
    setIsHarbourClub,
    isBiz4Biz,
    setIsBiz4Biz,
    tutorialStep,
    setTutorialStep,
    syncFirebaseAuth,
  };
}
